import { lazy, Suspense, useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import type { Plan } from "@edu/shared";
import * as api from "../../lib/api.ts";
import { Loading } from "../../lib/loading.tsx";

/**
 * Excalidraw ленивым чанком, а сама страница нет: так запрос заготовки и
 * загрузка доски идут параллельно. На 128 кбит/с последовательность «сначала
 * чанк, потом запрос» стоила бы лишних секунд на ровном месте. Тот же приём,
 * что на экране архива.
 */
const PlanBoard = lazy(() => import("./PlanBoard.tsx").then((m) => ({ default: m.PlanBoard })));

/** Экран правки заготовки: слайды и рисунок поверх них. */
export function PlanEditorPage() {
  const { planId = "" } = useParams();
  const [plan, setPlan] = useState<Plan | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const loaded = await api.getPlan(planId);
        if (alive) setPlan(loaded);
      } catch (e) {
        if (!alive) return;
        setErr(
          e instanceof api.ApiError && e.status === 404
            ? "Заготовка не найдена"
            : e instanceof Error
              ? e.message
              : String(e),
        );
      }
    })();
    return () => {
      alive = false;
    };
  }, [planId]);

  if (err !== null) {
    return (
      <main className="lobby">
        <h1>Заготовка</h1>
        <p className="err">{err}</p>
        <Link className="btn" to="/plans">
          К списку заготовок
        </Link>
      </main>
    );
  }

  if (plan === null) {
    return (
      <main className="lobby">
        <h1>Заготовка</h1>
        <Loading />
      </main>
    );
  }

  return (
    <div className="room-shell">
      {/*
        Панель инструментов доски под карточкой, как в комнате урока
        (`TOOLBAR_SIDE` в `room/room-options.ts`). Литерал, а не та константа:
        у редактора есть только нижнее положение, правила верхнего считаются
        от шапки комнаты, которой здесь нет. Разбор у `.plan-edit[data-toolbar]`
        в `styles.css`
      */}
      <div className="plan-edit" data-toolbar="down">
        {/* Обёртки .board здесь нет намеренно: со слайдами доска это не только
            холст, но и полоса навигации, и раскладку между ними задаёт она */}
        <Suspense fallback={<Loading />}>
          {/*
            ⚠️ `key` по заготовке обязателен. Баннер озвучки ведёт кнопкой
            «Открыть заготовку» из одного редактора прямо в другой, а маршрут тот
            же, то есть страница НЕ перемонтируется. Без ключа редактор принял бы
            новую заготовку в старый компонент: слайды прежней в ref, её же
            ревизия, а сохранять стал бы уже под новым id. При совпавшей
            ревизии это затёрло бы вторую заготовку слайдами первой, при
            несовпавшей остановило бы сохранение отказом не по делу. Постановка озвучки
            (`use-tts-placement.ts`) тоже держится на том, что одно монтирование
            это одна заготовка
          */}
          <PlanBoard key={plan.id} plan={plan} />
        </Suspense>
      </div>
    </div>
  );
}
